import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaChartLine,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaCouch,
  FaCubes,
  FaDesktop,
  FaDraftingCompass,
  FaExchangeAlt,
  FaLightbulb,
  FaPaintRoller,
  FaPalette,
  FaPlay,
  FaProjectDiagram,
  FaUsers,
  FaVideo,
  FaVrCardboard
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const videoSrc = '/inoviqspace-demo.mp4';
const posterSrc = '/inoviqspace-poster.jpg';

const industryUseCases = [
  {
    icon: FaPaintRoller,
    title: 'Interior Design',
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Modern interior living room design visualization',
    features: ['Live material changes', 'Paint and finish previews', 'Lighting mood review', 'Client approval walkthroughs'],
    need: 'Interior clients often struggle to imagine the final result from drawings, mood boards, or static renders.',
    value: 'Designers can sell concepts more confidently by changing materials, colors, furniture, and lighting live during the meeting.',
    whyUse: 'Use InoviqSpace to reduce confusion, shorten revision cycles, and help clients approve interiors before buying materials or starting site work.'
  },
  {
    icon: FaBuilding,
    title: 'Real Estate',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Real estate property presentation for buyers',
    features: ['Virtual property tours', 'Pre-construction preview', 'Remote buyer demos', 'Premium sales presentation'],
    need: 'Buyers want confidence before visiting a site, booking a unit, or investing in a property that is still under development.',
    value: 'Sales teams can convert floor plans into interactive demos, show lifestyle value, and present properties to remote leads with more impact.',
    whyUse: 'Use InoviqSpace to reduce dependency on physical sample flats and turn real estate presentations into guided virtual buying experiences.'
  },
  {
    icon: FaLightbulb,
    title: 'Lighting Companies',
    image: 'https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Decorative interior lighting fixture in a premium room',
    features: ['Color temperature control', 'Intensity adjustment', 'Day/night comparison', 'Real-time fixture testing'],
    need: 'Lighting is difficult to judge from catalog images because ambience, brightness, color temperature, and room materials change the final feel.',
    value: 'Lighting brands can demonstrate real ambience, compare products live, and justify premium fixtures through realistic room-level impact.',
    whyUse: 'Use InoviqSpace to help customers understand how a light will actually perform in a space before purchase or installation.'
  },
  {
    icon: FaCouch,
    title: 'Furniture Showrooms',
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Furniture showroom with sofa and styled living room setup',
    features: ['Virtual showroom', 'Product placement', 'Color and finish options', 'Space-fit preview'],
    need: 'Physical showrooms have limited display space, while customers want to see multiple furniture variants inside their own style of room.',
    value: 'Showrooms can present more products digitally, support remote consultation, and create a try-before-you-buy buying experience.',
    whyUse: 'Use InoviqSpace to increase product discovery without moving physical inventory or rebuilding showroom layouts.'
  },
  {
    icon: FaDraftingCompass,
    title: 'Architectural Visualization Studios',
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Architectural visualization planning and model review workflow',
    features: ['SketchUp / FBX import', 'Ray tracing', 'Path tracing', '4K render export'],
    need: 'ArchViz clients increasingly expect more than static images; they want interactive reviews, VR walkthroughs, and faster design iteration.',
    value: 'Studios can offer a higher-ticket interactive service with model upload, real-time review, VR walkthroughs, and polished render output.',
    whyUse: 'Use InoviqSpace as a premium delivery layer for client review, sales presentation, and interactive visualization projects.'
  }
];

const coreFeatures = [
  'Native desktop application workflow',
  'Unity HDRP with real-time ray tracing',
  'Cinematic path tracing render pipeline',
  '1K / 2K / 4K HD render export',
  'SketchUp / FBX / interior model upload',
  'Real-time material, paint, and furniture customization',
  'Dynamic day/night and sun direction system',
  'Lighting temperature and intensity controls',
  'VR walkthrough experience',
  'High-quality interactive ArchViz workflow',
  'Optimized native rendering performance'
];

const featureStack = [
  {
    icon: FaDesktop,
    title: 'Native desktop application',
    description: 'Built for showroom PCs, sales rooms, client presentations, and high-performance design review sessions.'
  },
  {
    icon: FaLightbulb,
    title: 'HDRP ray-traced visualization',
    description: 'Real-time ray tracing, path tracing, dynamic lighting, and realistic material response for premium interiors.'
  },
  {
    icon: FaCloudUploadAlt,
    title: 'Model upload workflow',
    description: 'Import SketchUp, FBX, and interior model assets so teams can turn project files into interactive spaces.'
  },
  {
    icon: FaPalette,
    title: 'Live customization tools',
    description: 'Change paint, materials, furniture, finishes, daylight, and lighting intensity during the presentation.'
  },
  {
    icon: FaVrCardboard,
    title: 'VR walkthrough experience',
    description: 'Review rooms in immersive mode for property demos, design approvals, and spatial decision-making.'
  },
  {
    icon: FaUsers,
    title: 'Collaborative review ecosystem',
    description: 'Multiplayer sessions, screen sharing, live communication, and synchronized editing for remote teams.'
  }
];

const collaborationFeatures = [
  {
    icon: FaUsers,
    title: 'Real-Time Multiplayer Collaboration',
    description: 'Multiple users can join the same interior workspace and review design decisions together.'
  },
  {
    icon: FaExchangeAlt,
    title: 'Live Model Synchronization',
    description: 'Uploaded SketchUp, FBX, or interior models can be shared with connected users in real time.'
  },
  {
    icon: FaCubes,
    title: 'Shared Collaborative Editing',
    description: 'Teams can adjust layouts, materials, colors, finishes, furniture placement, and lighting together.'
  },
  {
    icon: FaVideo,
    title: 'Cross-Device Screen Sharing',
    description: 'Presentation sessions support desktop and mobile review workflows for remote collaboration.'
  }
];

const communicationFeatures = [
  'Live video calling',
  'In-app live chat',
  'Real-time meeting system',
  'Collaborative virtual walkthroughs'
];

const marketDrivers = [
  {
    icon: FaChartLine,
    title: 'Real estate marketing',
    description: 'Projects can be sold and explained before construction is finished, giving buyers a clearer vision of the final space.'
  },
  {
    icon: FaPaintRoller,
    title: 'Interior presentation and approval',
    description: 'Clients can compare materials, colors, furniture, and lighting live, reducing uncertainty and long revision cycles.'
  },
  {
    icon: FaCouch,
    title: 'Virtual showrooms',
    description: 'Businesses can present more products and room styles digitally without expanding physical showroom inventory.'
  },
  {
    icon: FaCubes,
    title: 'Smart home and digital twin workflows',
    description: 'Interactive rooms can support future digital twin, smart space planning, and product configuration experiences.'
  },
  {
    icon: FaVrCardboard,
    title: 'VR-based property experience',
    description: 'Immersive walkthroughs help clients understand scale, mood, layout, and space flow in a more memorable way.'
  }
];

const businessOutcomes = [
  'Reduce physical showroom costs',
  'Improve client engagement and confidence',
  'Speed up design approval processes',
  'Deliver realistic pre-construction visualization',
  'Create premium interactive experiences'
];

const futurePotential = [
  'Metaverse-ready spatial experiences',
  'XR / VR technology adoption',
  'Digital twin ecosystems',
  'Real-time rendering pipelines'
];

const nativeAdvantages = [
  {
    title: 'Native performance',
    description: 'A desktop-native workflow can handle heavier interiors, real-time lighting, ray tracing, VR review, and smooth client demos better than a lightweight web preview.'
  },
  {
    title: 'Showroom ready',
    description: 'It can run on office PCs, sales rooms, exhibition kiosks, and client presentation systems as a dedicated premium visualization app.'
  },
  {
    title: 'High-quality output',
    description: 'Teams can use the same platform for live interaction, cinematic path tracing, and 1K / 2K / 4K render exports.'
  },
  {
    title: 'Business scalable',
    description: 'Model upload, multiplayer review, screen sharing, VR, and communication tools make it suitable for real commercial workflows, not just demo videos.'
  }
];

const InoviqSpace: React.FC = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const inoviqSpaceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'InoviqSpace',
    applicationCategory: 'DesignApplication',
    operatingSystem: 'Windows',
    url: `${siteUrl}/inoviqspace`,
    image: `${siteUrl}/inoviqspace-poster.jpg`,
    description: 'InoviqSpace is a native Unity HDRP interior visualization and virtual showroom platform for interior design, real estate, lighting, furniture showrooms, and ArchViz studios.',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/PreOrder',
      url: `${siteUrl}/contact?service=InoviqSpace%20Early%20Demo%20Request`
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24">
      <SEO
        title="InoviqSpace - Interactive Interior Visualization & Virtual Showroom"
        description="InoviqSpace is a native Unity HDRP interior visualization and virtual showroom platform for interior design, real estate, lighting companies, furniture showrooms, and ArchViz studios."
        path="/inoviqspace"
        image="/inoviqspace-poster.jpg"
        keywords={[
          'InoviqSpace',
          'interior visualization software',
          'virtual showroom development',
          'Unity HDRP',
          'ray tracing visualization',
          'VR walkthrough',
          'collaborative design platform',
          'real estate virtual showroom',
          'furniture showroom visualization',
          'lighting visualization software',
          'architectural visualization platform'
        ]}
        jsonLd={inoviqSpaceJsonLd}
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(6,182,212,0.12),transparent_26rem),radial-gradient(circle_at_78%_20%,rgba(168,85,247,0.16),transparent_26rem),radial-gradient(circle_at_50%_78%,rgba(239,68,68,0.12),transparent_30rem),linear-gradient(rgba(255,255,255,0.012)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.012)_1px,transparent_1px)] bg-[size:auto,auto,auto,82px_82px,82px_82px]" />
      <div className="pointer-events-none absolute left-[-8rem] top-32 h-80 w-80 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[-8rem] top-[34rem] h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-cyan-400" />
                </span>
                <span className="font-display text-sm font-semibold text-cyan-200">Demo Under Development</span>
              </div>

              <h1 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
                InoviqSpace
                <span className="mt-2 block bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Interactive Interior Visualization & Virtual Showroom
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-300 sm:text-xl">
                InoviqSpace is a native Unity HDRP platform for immersive interior visualization, virtual showrooms,
                VR walkthroughs, real-time collaboration, and complete design review workflows.
              </p>

              <div className="mt-8">
                <Link
                  to="/contact?service=InoviqSpace%20Early%20Demo%20Request"
                  className="group inline-flex items-center gap-3 rounded-full border border-cyan-300/40 bg-gradient-to-r from-cyan-500 to-blue-600 px-7 py-4 font-semibold text-white shadow-[0_0_30px_rgba(34,211,238,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(34,211,238,0.4)]"
                >
                  Request Early Demo
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                {['Unity HDRP', 'Ray Tracing', 'Path Tracing', 'VR Ready', 'Real-Time Multiplayer'].map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-gray-200 backdrop-blur-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="animate-scale-in">
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] p-3 shadow-2xl shadow-cyan-500/10 backdrop-blur-xl">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(34,211,238,0.18),transparent_34%),radial-gradient(circle_at_80%_90%,rgba(168,85,247,0.16),transparent_32%)]" />
                <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
                  {isVideoLoaded ? (
                    <video
                      src={videoSrc}
                      className="aspect-video w-full bg-black object-cover"
                      controls
                      autoPlay
                      playsInline
                      poster={posterSrc}
                      preload="auto"
                      onErrorCapture={(event) => {
                        event.stopPropagation();
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsVideoLoaded(true)}
                      className="group relative block aspect-video w-full overflow-hidden bg-black text-left"
                      aria-label="Play InoviqSpace demo video"
                    >
                      <img
                        src={posterSrc}
                        alt="InoviqSpace demo preview"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/20" />
                      <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-cyan-200/40 bg-cyan-400/20 text-white shadow-2xl shadow-cyan-500/30 backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:bg-cyan-400/30">
                        <FaPlay className="ml-1 text-2xl" />
                      </span>
                      <span className="absolute bottom-5 left-5 rounded-full border border-white/15 bg-black/65 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md">
                        Development demo preview
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
          <div className="mb-10 max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-400/10 px-5 py-2 backdrop-blur-sm">
              <FaCubes className="text-purple-200" />
              <span className="font-display text-sm font-semibold text-purple-200">Platform Features</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              A native visualization platform built for sales, approval, and collaboration
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">
              InoviqSpace combines real-time rendering, customization, VR, model upload, and multiplayer collaboration
              so businesses can present spaces as an interactive product experience, not just a static render.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {featureStack.map((feature, index) => (
              <div
                key={feature.title}
                className="group rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-purple-300/40 hover:shadow-2xl hover:shadow-purple-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.06}s` }}
              >
                <feature.icon className="text-4xl text-purple-200 transition-transform duration-300 group-hover:scale-110" />
                <h3 className="mt-5 font-display text-2xl font-bold text-white">{feature.title}</h3>
                <p className="mt-3 leading-relaxed text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mb-12 max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2 backdrop-blur-sm">
              <FaProjectDiagram className="text-cyan-200" />
              <span className="font-display text-sm font-semibold text-cyan-200">Market Need & Value</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Built for businesses that need clients to see before they buy
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">
              InoviqSpace is not only a visualization demo. It is a native interactive sales and approval platform for
              teams that need faster decisions, better presentations, and realistic digital experiences before physical
              production, showroom setup, or construction starts.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {industryUseCases.map((industry, index) => (
              <div
                key={industry.title}
                className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-cyan-400/40 hover:shadow-2xl hover:shadow-cyan-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={industry.image}
                    alt={industry.imageAlt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
                  <div className="absolute left-5 top-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-200/30 bg-black/45 text-cyan-200 shadow-xl shadow-black/30 backdrop-blur-md">
                    <industry.icon className="text-2xl transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="font-display text-2xl font-bold text-white">{industry.title}</h3>
                    <p className="mt-1 text-sm font-semibold text-cyan-100">Sales-ready interactive visualization</p>
                  </div>
                </div>

                <div className="p-7">
                  <div className="flex flex-wrap gap-2">
                    {industry.features.map((feature) => (
                      <span
                        key={feature}
                        className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-100"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 space-y-5">
                    <div>
                      <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
                        Market Need
                      </p>
                      <p className="mt-2 leading-relaxed text-gray-300">{industry.need}</p>
                    </div>

                    <div>
                      <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-purple-200">
                        Business Value
                      </p>
                      <p className="mt-2 leading-relaxed text-gray-300">{industry.value}</p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
                      <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-red-200">
                        Why Use It
                      </p>
                      <p className="mt-2 leading-relaxed text-gray-200">{industry.whyUse}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-400/10 px-5 py-2 backdrop-blur-sm">
              <FaDesktop className="text-purple-200" />
              <span className="font-display text-sm font-semibold text-purple-200">Core Platform</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Built for high-quality interactive ArchViz workflows
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">
              InoviqSpace gives businesses a premium way to design, visualize, review, and present interiors before
              committing to physical production or construction.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {coreFeatures.map((feature) => (
              <div
                key={feature}
                className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl transition-all duration-300 hover:border-cyan-400/40 hover:bg-white/[0.07]"
              >
                <FaCheckCircle className="mt-1 flex-shrink-0 text-cyan-300" />
                <p className="leading-relaxed text-gray-200">{feature}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-[2rem] border border-white/10 bg-gradient-to-br from-cyan-500/10 via-white/[0.04] to-purple-500/10 p-6 backdrop-blur-xl sm:p-8">
            <div className="max-w-3xl">
              <h3 className="font-display text-3xl font-bold text-white sm:text-4xl">
                Why a native application matters for this market
              </h3>
              <p className="mt-4 text-lg leading-relaxed text-gray-400">
                For interior, real estate, lighting, furniture, and ArchViz teams, the value is not only visual quality.
                The real value is a reliable presentation tool that can run smoothly in sales meetings, showrooms,
                exhibitions, and client review sessions.
              </p>
            </div>

            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {nativeAdvantages.map((advantage) => (
                <div
                  key={advantage.title}
                  className="rounded-2xl border border-white/10 bg-black/25 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-300/40"
                >
                  <h4 className="font-display text-xl font-bold text-white">{advantage.title}</h4>
                  <p className="mt-3 leading-relaxed text-gray-400">{advantage.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2 backdrop-blur-sm">
                <FaUsers className="text-cyan-200" />
                <span className="font-display text-sm font-semibold text-cyan-200">Collaborative Design Ecosystem</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                From visualization demo to live sales and review room
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-400">
                Multiple users can join the same virtual space, upload models, share screens, edit materials, reposition
                furniture, control lighting, and review decisions together. This makes the platform useful for sales
                teams, designers, clients, and remote stakeholders inside one shared workflow.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {collaborationFeatures.map((feature, index) => (
                <div
                  key={feature.title}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-cyan-400/40 hover:shadow-2xl hover:shadow-cyan-500/10 animate-scale-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-3xl bg-gradient-to-br from-cyan-400/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <feature.icon className="text-4xl text-cyan-300 transition-transform duration-300 group-hover:scale-110" />
                  <h3 className="mt-5 font-display text-2xl font-bold text-white">{feature.title}</h3>
                  <p className="mt-3 leading-relaxed text-gray-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid gap-8 xl:grid-cols-3">
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-8 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-400/30 bg-red-400/10 px-5 py-2">
                <FaVideo className="text-red-300" />
                <span className="font-display text-sm font-semibold text-red-200">Built-In Communication</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                Communicate, review, and close decisions inside the same workspace
              </h2>
              <p className="mt-4 leading-relaxed text-gray-400">
                Sales and design teams can present remotely, explain options live, and keep the conversation inside the
                same visual context.
              </p>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                {communicationFeatures.map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/30 p-4 text-gray-200">
                    <FaCheckCircle className="flex-shrink-0 text-red-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-8 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2">
                <FaChartLine className="text-cyan-200" />
                <span className="font-display text-sm font-semibold text-cyan-200">Sales Outcomes</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                Business value clients can understand quickly
              </h2>
              <div className="mt-7 space-y-4">
                {businessOutcomes.map((item) => (
                  <div key={item} className="flex items-center gap-3 text-gray-200">
                    <FaLightbulb className="flex-shrink-0 text-cyan-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-8 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-400/10 px-5 py-2">
                <FaProjectDiagram className="text-purple-200" />
                <span className="font-display text-sm font-semibold text-purple-200">Market Demand</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                Made for growing immersive sales markets
              </h2>
              <div className="mt-7 space-y-5">
                {marketDrivers.map((driver) => (
                  <div key={driver.title} className="flex gap-4">
                    <driver.icon className="mt-1 flex-shrink-0 text-purple-200" />
                    <div>
                      <h3 className="font-display text-lg font-bold text-white">{driver.title}</h3>
                      <p className="mt-1 leading-relaxed text-gray-400">{driver.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap gap-2">
                {futurePotential.map((item) => (
                  <span key={item} className="rounded-full border border-purple-300/20 bg-purple-400/10 px-3 py-1 text-xs font-semibold text-purple-100">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container relative z-10 mx-auto px-4 py-16 pb-20 sm:px-6 sm:py-20 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-cyan-500/15 via-white/[0.04] to-purple-500/10 px-6 py-12 text-center shadow-2xl shadow-black/20 backdrop-blur-xl sm:px-10 sm:py-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.22),_transparent_55%)]" />
            <div className="relative">
              <FaVrCardboard className="mx-auto text-5xl text-cyan-300" />
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                InoviqSpace demo is under development
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-300">
                The demo is still being refined, but early walkthrough requests are open for businesses that want to
                review the visualization, customization, collaboration, VR, rendering, and communication workflows.
              </p>
              <Link
                to="/contact?service=InoviqSpace%20Early%20Demo%20Request"
                className="group mt-8 inline-flex items-center gap-3 rounded-full border border-cyan-300/40 bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(34,211,238,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(34,211,238,0.4)]"
              >
                Request Early Demo
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InoviqSpace;
